import { useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { useRouter } from 'next/router';

//types
import type { FormatDateType } from '@/components/pages/settings/subscription-invoices/PageContent/Components/RightSection';
import type { ActivePlanType } from '@/types/pricingTypes/subscriptionType';
import type { Dispatch, SetStateAction } from 'react';
import type { SubscriptionStateType } from '@/lib/settings/subscriptionInvoices/subscriptionState';
import type { URLStepType } from '@/components/pages/settings/subscription-invoices/PageContent/types';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import usePrices from '@/utils/hooks/swr/pricing/usePrices';
import useReceipts from '@/utils/hooks/swr/pricing/useReceipts';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

//lib
import { subscriptionStates } from '@/lib/settings/subscriptionInvoices/subscriptionState';

// Utils
import { post } from '@/utils/client';

// Components
import ConfirmationModal from '@/components/composite/Modals/ConfirmationModal';
import Button from '@/components/base/Button';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';

type SubscriptionProps = {
    formatDate: FormatDateType;
    togglePlanModal: () => void;
};
export function Subscription({ formatDate, togglePlanModal }: SubscriptionProps) {
    const { t } = useTranslation('settings');
    const router = useRouter();
    const { clearUrlQueriesWithoutReload } = useRouterQuery();
    const { step } = router.query;
    const urlStep = step as URLStepType;
    const { isOpen, toggle } = useToggle();
    const [{ isApiLoading, hasApiError }, setApiState] = useState<{
        isApiLoading: boolean;
        hasApiError: string;
    }>({
        isApiLoading: false,
        hasApiError: null
    });
    const { subscription, mutateSubscription } = useSubscription();
    const { mutatePrices } = usePrices();
    const { mutateReceipts } = useReceipts();

    const closeModal = () => {
        if (isApiLoading) {
            return null;
        }
        toggle();
    };

    function getModalData() {
        if (subscription.product_key === 'trial') {
            return subscriptionStates.default;
        }

        if (subscription.is_waiting_for_cancel) {
            return subscriptionStates.is_waiting_for_cancel;
        }
        if (subscription.is_waiting_for_downgrade) {
            return subscriptionStates.is_waiting_for_downgrade;
        }
        return subscriptionStates.default;
    }

    const [modalData, setModalData] = useState<SubscriptionStateType>(getModalData());

    useEffect(() => {
        if (
            !subscription.product_key ||
            subscription.product_key === 'trial' ||
            subscription.is_manual
        ) {
            return undefined;
        }
        if (urlStep !== 'cancel_action_from_plan_modal') {
            return undefined;
        }
        if (isOpen) {
            return undefined;
        }
        clearUrlQueriesWithoutReload();
        toggle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlStep]);

    if (!subscription.product_key) {
        return (
            <>
                <p
                    className="mb-2 text-sm text-icoGray-500 dark:text-icoDarkMode-wolf"
                    data-testid="subscription-no-plan">
                    {t('subscription_invoices_page.next_renewal')}
                </p>
                <p>{t('subscription_invoices_page.no_active_plan')}</p>
                <button
                    data-testid="button-choose-plan-subscription"
                    onClick={togglePlanModal}
                    className="text-xs text-icoBlue underline focus:ring-0 focus:ring-offset-0">
                    {t('subscription_invoices_page.choose_your_plan')}
                </button>
            </>
        );
    }

    if (subscription.product_key === 'trial') {
        return (
            <>
                <p
                    className="mb-2 text-sm text-icoGray-500 dark:text-icoDarkMode-wolf"
                    data-testid="subscription-plan-trial">
                    {t('subscription_invoices_page.end_trial')}
                </p>
                <p className="text-sm">{formatDate(subscription.trial_ends_at)}</p>
            </>
        );
    }

    if (subscription.is_manual) {
        return (
            <>
                <p
                    className="mb-2 text-sm text-icoGray-500 dark:text-icoDarkMode-wolf"
                    data-testid="subscription-manual">
                    {t('subscription_invoices_page.end_the')}
                </p>
                <p className="text-sm">{formatDate(subscription.next_renewal)}</p>
            </>
        );
    }

    const modalSubmit = async () => {
        setApiState({ isApiLoading: true, hasApiError: null });
        try {
            await post(modalData.apiEndpoint);
            await Promise.all([mutateSubscription(), mutatePrices(), mutateReceipts()]);
            setApiState({ isApiLoading: false, hasApiError: null });
        } catch (error) {
            const apiError = error as Error;
            setApiState({ isApiLoading: false, hasApiError: apiError.message });
        } finally {
            closeModal();
        }
    };

    return (
        <>
            {hasApiError ? (
                <CompoundBanner type="error" data-testid="error-cancel-request">
                    <CompoundBanner.Icon icon="warning" />
                    <CompoundBanner.Content
                        className="mb-2"
                        title={t('errors:error_occured')}
                        message={t('errors:error_occured_submit_data')}
                    />
                </CompoundBanner>
            ) : null}

            <SubscriptionContent subscription={subscription} formatDate={formatDate} />
            <ActionsAvailable
                subscription={subscription}
                getModalData={getModalData}
                setModalData={setModalData}
                toggle={toggle}
            />
            <ConfirmationModal
                isOpen={isOpen}
                onSubmit={modalSubmit}
                closeModal={closeModal}
                isLoading={isApiLoading}
                title={t(modalData.title)}>
                {t(modalData.modalText)}
            </ConfirmationModal>
        </>
    );
}

type ActionsAvailableProps = {
    subscription: ActivePlanType;
    setModalData: Dispatch<SetStateAction<SubscriptionStateType>>;
    toggle: () => void;
    getModalData: () => SubscriptionStateType;
};
function ActionsAvailable({
    subscription,
    setModalData,
    toggle,
    getModalData
}: ActionsAvailableProps) {
    const { t } = useTranslation('settings');

    if (subscription.is_waiting_for_cancel || subscription.is_waiting_for_downgrade) {
        const getButtonText = () => {
            if (subscription?.is_waiting_for_cancel) {
                return subscriptionStates.is_waiting_for_cancel.title;
            }

            return subscriptionStates.is_waiting_for_downgrade.title;
        };
        return (
            <Button
                data-testid="button-toggle-modal"
                variant="secondary"
                className="mt-3 w-full"
                onClick={() => {
                    setModalData(getModalData());
                    toggle();
                }}>
                {t(getButtonText())}
            </Button>
        );
    }

    return (
        <button
            className="pendo-cancel-flow text-xs text-icoBlue underline focus:ring-0 focus:ring-offset-0"
            data-testid="button-toggle-cancel">
            {t(subscriptionStates.default.title)}
        </button>
    );
}

type SubscriptionContentProps = {
    subscription: ActivePlanType;
    formatDate: SubscriptionProps['formatDate'];
};
function SubscriptionContent({ subscription, formatDate }: SubscriptionContentProps) {
    const { t } = useTranslation('settings');

    if (subscription.is_waiting_for_cancel) {
        return (
            <CompoundBanner type="error" data-testid="cancel-pending">
                <CompoundBanner.Icon icon="warning" />
                <CompoundBanner.Content>
                    <p className="font-GMedium text-sm">
                        <Trans
                            i18nKey="settings:subscription_invoices_page.new_pricing.subscription.waiting_for_cancel_notice"
                            components={{ 0: <span className="font-GBold" /> }}
                            values={{ date: formatDate(subscription.next_renewal) }}
                        />
                    </p>
                </CompoundBanner.Content>
            </CompoundBanner>
        );
    }

    if (subscription.is_waiting_for_downgrade) {
        return (
            <CompoundBanner type="warning" data-testid="downgrade-pending">
                <CompoundBanner.Icon icon="warning" />
                <CompoundBanner.Content>
                    <p className="font-GMedium text-sm">
                        <Trans
                            i18nKey="settings:subscription_invoices_page.new_pricing.subscription.waiting_for_downgrade_notice"
                            components={{ 0: <span className="font-GBold" /> }}
                            values={{ date: formatDate(subscription.next_renewal) }}
                        />
                    </p>
                </CompoundBanner.Content>
            </CompoundBanner>
        );
    }

    return (
        <>
            <p
                className="mb-2 text-sm text-icoGray-500 dark:text-icoDarkMode-wolf"
                data-testid="subscription-details">
                {t('subscription_invoices_page.next_renewal')}
            </p>
            <p className="mb-4 text-sm">{formatDate(subscription.next_renewal)}</p>
        </>
    );
}
